import axios from 'axios';
import emailAccountTypes from '../../Email-Integrations/config/email-account-types';

const initialState = {
  isShownEmailManagementDrawer: false,
  isShownConnectionDrawer: false,
  emailAccounts: [],
  gmailAccounts: [],
  outlookAccounts: [],
  exchangeAccounts: [],
  imapAccounts: [],
  features: [],
  exchangeConnectionScopes: '',
  exchangeEmailConnected: '',
  outlookEmailSelected: '',
  imapEmailSelected: '',
  error: '',
  isEmailAccountsLoading: false,
  googleEmailConnected: '',
  imapHelpSelectedTab: 0,
};
const EMAIL_INTEGRATION = 'email_integration_notification_dismissed';
const PERSONAL_EMAIL_DOMAINS = ['outlook.com', 'hotmail.com', 'live.com', 'msn.com'];
const setDismissedBanner = () => {
  localStorage.setItem(EMAIL_INTEGRATION, true);
};

export default {
  namespaced: true,
  state: { ...initialState },
  mutations: {
    setShowEmailManagementDrawer(state, boolean) {
      state.isShownEmailManagementDrawer = boolean;
    },
    setShowConnectionDrawer(state, boolean) {
      state.isShownConnectionDrawer = boolean;
    },
    setEmailAccounts(state, accounts) {
      state.emailAccounts = accounts;
    },
    removeEmailAccount(state, removedAccountID) {
      state.emailAccounts = state
        .emailAccounts
        .filter(({ id }) => id !== removedAccountID);
    },
    // Gmail
    setGmailAccounts(state, accounts) {
      state.gmailAccounts = accounts;
    },
    removeGmailAccount(state, removedAccountID) {
      state.gmailAccounts = state
        .gmailAccounts
        .filter(({ id }) => id !== removedAccountID);
    },
    // Outlook
    setOutlookAccounts(state, accounts) {
      state.outlookAccounts = accounts;
    },
    setOutlookEmailSelected(state, email) {
      state.outlookEmailSelected = email;
    },
    clearOutlookEmailSelected(state) {
      state.outlookEmailSelected = '';
    },
    removeOutlookAccount(state, removedAccountID) {
      state.outlookAccounts = state
        .outlookAccounts
        .filter(({ id }) => id !== removedAccountID);
    },
    // Exchange
    setExchangeAccounts(state, accounts) {
      state.exchangeAccounts = accounts;
    },
    removeExchangeAccount(state, removedAccountID) {
      state.exchangeAccounts = state
        .exchangeAccounts
        .filter(({ id }) => id !== removedAccountID);
    },
    setFeatures(state, features) {
      state.features = features;
    },
    setExchangeEmailConnected(state, email) {
      state.exchangeEmailConnected = email;
    },
    setGoogleEmailConnected(state, email) {
      state.googleEmailConnected = email;
    },
    setExchangeConnectionScopes(state, scopes) {
      state.exchanceConnectionScopes = scopes;
    },
    // IMAP
    setIMAPAccounts(state, accounts) {
      state.imapAccounts = accounts;
    },
    setIMAPEmailSelected(state, email) {
      state.imapEmailSelected = email;
    },
    clearIMAPEmailSelected(state) {
      state.imapEmailSelected = '';
    },
    removeIMAPAccount(state, removedAccountID) {
      state.imapAccounts = state
        .imapAccounts
        .filter(({ id }) => id !== removedAccountID);
    },
    // Error
    setError(state, error) {
      state.error = error;
    },
    setIsEmailAccountsLoading(state, isLoading) {
      state.isEmailAccountsLoading = isLoading;
    },
    setImapHelpSelectedTab(state, value) {
      state.imapHelpSelectedTab = value;
    }
  },
  getters: {
    hasAtLeastOneGmailAccount: state => {
      return state.gmailAccounts && state.gmailAccounts.length;
    },
    hasAtLeastOneEmailAccount: state => {
      return state.emailAccounts && state.emailAccounts.length;
    },
    hasAtLeastOneOutlookAccount: state => {
      return state.outlookAccounts && state.outlookAccounts.length;
    },
    hasAtLeastOneExchangeAccount: state => {
      return state.exchangeAccounts && state.exchangeAccounts.length;
    },
    gmailFeatures: state => {
      // eslint-disable-next-line max-len
      return state.features.filter(feature => feature.email_account_type_id === emailAccountTypes.gmail.id);
    },
    outlookFeatures: state => {
      // eslint-disable-next-line max-len
      return state.features.filter(feature => feature.email_account_type_id === emailAccountTypes.outlook.id);
    },
    exchangeFeatures: state => {
      // eslint-disable-next-line max-len
      return state.features.filter(feature => feature.email_account_type_id === emailAccountTypes.exchange.id);
    },
    getEmailAccountByEmail: state => email => {
      return state.emailAccounts.find(emailAccount => emailAccount.email === email);
    },
    hasAtLeastOneOutlookAccountToReconnect: state => {
      return state.emailAccounts && state.emailAccounts.some(emailAccount => (emailAccount.needs_to_reconnect_microsoft && emailAccount.updated_at.Time <= '2025-06-08T00:00:00Z'));
    },
  },
  actions: {
    async resetErrorMessage({ commit }) {
      commit('setError', '');
    },
    // Gmail
    async initGmailConnection({ commit, _ }, scopes) {
      try {
        const { data: { oauth_url: oauthURL } } = await axios.get(
          `${process.env.VUE_APP_NYLAS_API_BASE_URL}/google/init-connection?scope=${scopes}`,
        );
        return oauthURL;
      } catch (error) {
        commit('setError', error);
      }
    },
    async connectGmailToNylas({ commit, dispatch }, payload) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_NYLAS_API_BASE_URL}/google/oauth?app=crm`,
          payload,
        );

        localStorage.setItem('googleEmailConnected', response.data.email);
        commit('setGoogleEmailConnected', response.data.email);

        if (this._actions['ldAppLayout/setHasAtLeastOneEmailAccount']) {
          dispatch('ldAppLayout/setHasAtLeastOneEmailAccount', {}, { root: true });
        }

        setDismissedBanner();
      } catch (error) {
        commit('setError', error.message);
      }
    },
    async loadEmailAccounts({ state, commit }, forceGet = false) {
      if (!forceGet && (state.emailAccounts.length || state.isEmailAccountsLoading)) return;

      commit('setIsEmailAccountsLoading', true);
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_NYLAS_API_BASE_URL}/email-accounts/`,
        );

        commit('setEmailAccounts', response.data.email_accounts);
      } catch (error) {
        commit('setError', error.message);
      }
      commit('setIsEmailAccountsLoading', false);
    },
    async getGmailAccounts({ commit }) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_NYLAS_API_BASE_URL}/email-accounts/gmail`,
        );

        commit('setGmailAccounts', response.data.email_accounts);
        commit('setEmailAccounts', response.data.email_accounts);
      } catch (error) {
        commit('setError', error.message);
      }
    },
    async disconnectGmail({ state, commit }, emailAccountId) {
      try {
        await axios.post(
          `${process.env.VUE_APP_NYLAS_API_BASE_URL}/disconnect?app=crm`,
          { email_account_id: emailAccountId },
        );

        commit('removeGmailAccount', emailAccountId);
        commit('removeEmailAccount', emailAccountId);
      } catch (error) {
        commit('setError', error.message);
      }
    },
    // Outlook
    async connectOutlookToNylas({ commit, dispatch }, payload) {
      // eslint-disable-next-line camelcase
      const { email, auth, feature_ids } = payload;

      let index = PERSONAL_EMAIL_DOMAINS.findIndex(element => email.toLowerCase().endsWith('@' + element));
      if (index < 0) {
        commit('setError', 'The email domain you’ve entered doesn’t correspond to a valid outlook domain. Make sure you are using a valid email domain from:  <strong>outlook.com</strong>, <strong>hotmail.com</strong>, <strong>live.com</strong>, or <strong>msn.com</strong>, or try using the Microsoft connection when connecting your email.');
        return;
      }

      try {
        await axios.post(
          `${process.env.VUE_APP_NYLAS_API_BASE_URL}/outlook/connect?app=crm`,
          { feature_ids },
          {
            headers: {
              'LD-Basic-Auth': auth,
            },
          },
        );
        if (this._actions['ldAppLayout/setHasAtLeastOneEmailAccount']) {
          dispatch('ldAppLayout/setHasAtLeastOneEmailAccount', {}, { root: true });
        }

        setDismissedBanner();
      } catch (err) {
        commit('setError', 'Email or password is invalid. Please, try again.');
      }
    },
    async getOutlookEmailAccounts({ commit }) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_NYLAS_API_BASE_URL}/email-accounts/outlook`,
        );

        commit('setOutlookAccounts', response.data.email_accounts);
        commit('setEmailAccounts', response.data.email_accounts);
      } catch (error) {
        commit('setError', error);
      }
    },
    async disconnectOutlook({ state, commit }, emailAccountId) {
      try {
        await axios.post(
          `${process.env.VUE_APP_NYLAS_API_BASE_URL}/disconnect?app=crm`,
          { email_account_id: emailAccountId },
        );

        commit('removeOutlookAccount', emailAccountId);
        commit('removeEmailAccount', emailAccountId);
      } catch (error) {
        commit('setError', error);
      }
    },
    // Exchange
    async connectExchangeToNylas({ commit, dispatch }, payload) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_NYLAS_API_BASE_URL}/exchange/connect?app=crm`,
          payload,
        );
        localStorage.setItem('exchangeEmailConnected', response.data.email);
        commit('setExchangeEmailConnected', response.data.email);

        if (this._actions['ldAppLayout/setHasAtLeastOneEmailAccount']) {
          dispatch('ldAppLayout/setHasAtLeastOneEmailAccount', {}, { root: true });
        }

        setDismissedBanner();
      } catch (error) {
        commit('setError', error.message);
      }
    },
    async getExchangeEmailAccounts({ commit }) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_NYLAS_API_BASE_URL}/email-accounts/office365`,
        );

        commit('setExchangeAccounts', response.data.email_accounts);
        commit('setEmailAccounts', response.data.email_accounts);
      } catch (error) {
        commit('setError', error);
      }
    },
    async disconnectExchange({ commit }, emailAccountId) {
      try {
        await axios.post(
          `${process.env.VUE_APP_NYLAS_API_BASE_URL}/disconnect?app=crm`,
          { email_account_id: emailAccountId },
        );

        commit('removeExchangeAccount', emailAccountId);
        commit('removeEmailAccount', emailAccountId);
      } catch (error) {
        commit('setError', error);
      }
    },
    // IMAP
    async connectIMAPToNylas({ commit, dispatch }, payload) {
      // eslint-disable-next-line camelcase
      const { smtp_host, smtp_port, imap_host, imap_port, imapAuth, smtpAuth, feature_ids } = payload;

      try {
        await axios.post(
          `${process.env.VUE_APP_NYLAS_API_BASE_URL}/imap/connect?app=crm`,
          { smtp_host, smtp_port, imap_host, imap_port, feature_ids },
          {
            headers: {
              'LD-IMAP-Basic-Auth': imapAuth,
              'LD-SMTP-Basic-Auth': smtpAuth,
            },
          },
        );
        if (this._actions['ldAppLayout/setHasAtLeastOneEmailAccount']) {
          dispatch('ldAppLayout/setHasAtLeastOneEmailAccount', {}, { root: true });
        }

        setDismissedBanner();
      } catch (err) {
        commit('setError', 'Error while trying to connect your email with the IMAP service. Verify your credentials and try again.');
      }
    },
    async getIMAPEmailAccounts({ commit }) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_NYLAS_API_BASE_URL}/email-accounts/imap`,
        );

        commit('setIMAPAccounts', response.data.email_accounts);
        commit('setEmailAccounts', response.data.email_accounts);
      } catch (error) {
        commit('setError', error);
      }
    },
    async disconnectIMAP({ state, commit }, emailAccountId) {
      try {
        await axios.post(
          `${process.env.VUE_APP_NYLAS_API_BASE_URL}/disconnect?app=crm`,
          { email_account_id: emailAccountId },
        );

        commit('removeIMAPAccount', emailAccountId);
        commit('removeEmailAccount', emailAccountId);
      } catch (error) {
        commit('setError', error);
      }
    },
    async getFeatures({ commit }) {
      try {
        const features = await axios.get(
          `${process.env.VUE_APP_NYLAS_API_BASE_URL}/email-features?app=crm`,
        );

        commit('setFeatures', features.data.email_features);
      } catch (error) {
        commit('setError', error);
      }
    },
  },
};
